import { create } from "zustand";
import { logger } from "@/utils/logger";
import { IStore, SelectedProduct } from "@/types/interfaces";
import { CreatorPriceData } from "@/types/Creator";
import axios from "axios";
import { UPLOAD_ZIP_REQUEST, UPLOAD_FILE_REQUEST } from "@/constants/requests";

interface CartState {
  /** Whether the cart is being updated */
  isLoading: boolean;

  selectedProduct?: SelectedProduct;

  pricingData?: Record<string, Record<string, CreatorPriceData>>;
}

interface CartActions extends IStore {
  // Actions
  addToCart: (file: Blob) => Promise<{ fileName: string }>;
  uploadScreenShot: (file: Blob, fileName: string) => Promise<string>;
  shareUpload: (
    file: Blob,
    fileName: string,
    creator: string
  ) => Promise<string>;
  setSelectedProduct: (product: SelectedProduct) => void;
  getSelectedProduct: () => SelectedProduct;
  setVariantBaseSize: (size: number) => void;
  getVariantBaseSize: () => number | undefined;
  setPricingData: (data: Record<string, object>) => void;
  getPricingData: () => Record<string, object> | undefined;
}

const initialState: CartState = {
  isLoading: false
};

export const useCartStore = create<CartState & CartActions>(
  (set: any, get: any) => ({
    ...initialState,

    initialize: async (): Promise<boolean> => {
      try {
        return true;
      } catch (error) {
        logger.error("Failed to initialize CartStore", error, "CartStore");
        return false;
      }
    },

    reset: () => {
      set(initialState);
    },

    getStatus: () => {
      const state = get();
      return {
        isInitialized: true,
        itemCount: state.items.length
      };
    },

    addToCart: async (zip: Blob): Promise<{ fileName: string }> => {
      try {
        const formData = new FormData();
        formData.append("zip", zip);

        const url = `${import.meta.env.VITE_BACKEND}${UPLOAD_ZIP_REQUEST}`;
        const response = await axios.post(url, formData);

        if (!response || !response.data) {
          throw new Error(`HTTP error`);
        }

        // Backend returns { fileName: string } with status 202
        return { fileName: response.data.fileName };
      } catch (error) {
        logger.error("Failed to add to cart", error, "CartStore");
        throw error;
      }
    },

    setSelectedProduct: (product: SelectedProduct) => {
      set((state: CartState) => {
        return {
          ...state,
          selectedProduct: product
        };
      });
    },

    getSelectedProduct: () => {
      const state = get();
      return state.selectedProduct;
    },

    setPricingData: (data: Record<string, object>) => {
      set((state: CartState) => {
        return {
          ...state,
          pricingData: data
        };
      });
    },

    getPricingData: () => {
      const state = get();
      return state.pricingData;
    },

    setVariantBaseSize: (size: number) => {
      set((state: CartState) => {
        return {
          ...state,
          variantBaseSize: size
        };
      });
    },

    getVariantBaseSize: () => {
      const state = get();
      return state.variantBaseSize;
    },

    uploadScreenShot: async (file: Blob, fileName: string): Promise<string> => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fileName", fileName);
        formData.append("folder", "screenshots");
        const url = `${import.meta.env.VITE_BACKEND}${UPLOAD_FILE_REQUEST}`;
        const response = await axios.post(url, formData);
        if (!response) {
          throw new Error(`HTTP error`);
        }

        const res = response.data;
        return res as string;
      } catch (error) {
        logger.error("Failed to upload screenshot", error, "CartStore");
        throw error;
      }
    },
    shareUpload: async (
      file: Blob,
      fileName: string,
      creator: string
    ): Promise<string> => {
      try {
        const formData = new FormData();
        formData.append("zip", file);
        formData.append("name", fileName);
        formData.append("folder", `shares/${creator}`);
        const url = `${import.meta.env.VITE_BACKEND}${UPLOAD_ZIP_REQUEST}`;
        const response = await axios.post(url, formData);
        if (!response) {
          throw new Error(`HTTP error`);
        }
        const res = response.data;
        return res as string;
      } catch (error) {
        logger.error("Failed to share upload", error, "CartStore");
        throw error;
      }
    }
  })
);
